/**
 * Register all mutation defaults for rehydration support.
 *
 * This file is imported at app startup to register mutation functions
 * with TanStack Query using `setMutationDefaults`. This allows mutations
 * to be properly restored after hydration from persistence.
 *
 * Note: PowerSync-based mutations (survey sites, observations, collections)
 * don't need registration here — PowerSync persists writes in its own queue.
 * Only non-PowerSync mutations that go directly to Supabase need registration.
 */

import { queryClient } from "./queryClient"
import { withScopeGate } from "./withScopeGate"

// Photo mutations
import {
  UPLOAD_PHOTO_MUTATION_KEY,
  uploadPhotoFn,
  onSuccessUploadPhoto,
} from "@/hooks/useUploadPhoto"
import {
  DELETE_PHOTO_MUTATION_KEY,
  deletePhotoFn,
  onSuccessDeletePhoto,
} from "@/hooks/useDeletePhoto"
import {
  UPDATE_PHOTO_CAPTION_MUTATION_KEY,
  updatePhotoCaptionFn,
  onSuccessUpdatePhotoCaption,
} from "@/hooks/useUpdatePhotoCaption"

// Track mutations
import {
  TRACK_DELETE_MUTATION_KEY,
  trackDeleteFn,
  onSuccessTrackDelete,
} from "@/hooks/useTrackDelete"

// Survey site mutations (PowerSync — registered for networkMode only)
import { createSurveySiteFn } from "@/hooks/useCreateSurveySite"
import {
  UPDATE_SURVEY_SITE_MUTATION_KEY,
  updateSurveySiteFn,
} from "@/hooks/useUpdateSurveySite"
import {
  DELETE_SURVEY_SITE_MUTATION_KEY,
  deleteSurveySiteFn,
} from "@/hooks/useDeleteSurveySite"
import { CREATE_SURVEY_SITE_MUTATION_KEY } from "./mutationKeys"

// Photo mutations
queryClient.setMutationDefaults(UPLOAD_PHOTO_MUTATION_KEY, {
  mutationFn: withScopeGate(uploadPhotoFn),
  onSuccess: onSuccessUploadPhoto,
})

queryClient.setMutationDefaults(DELETE_PHOTO_MUTATION_KEY, {
  mutationFn: withScopeGate(deletePhotoFn),
  onSuccess: onSuccessDeletePhoto,
})

queryClient.setMutationDefaults(UPDATE_PHOTO_CAPTION_MUTATION_KEY, {
  mutationFn: withScopeGate(updatePhotoCaptionFn),
  onSuccess: onSuccessUpdatePhotoCaption,
})

// Track mutations
//
// Saving a track is deliberately not a persisted mutation: completeTrack
// writes the track to IndexedDB and lib/tracks/publishTracks.ts hands it to
// PowerSync, whose own queue does the uploading. See useActiveTrack.completeTrack
// and useTrackPublisher.
queryClient.setMutationDefaults(TRACK_DELETE_MUTATION_KEY, {
  mutationFn: trackDeleteFn,
  networkMode: "always",
  onSuccess: onSuccessTrackDelete,
})

// Survey site mutations (PowerSync)
queryClient.setMutationDefaults(CREATE_SURVEY_SITE_MUTATION_KEY, {
  mutationFn: createSurveySiteFn,
  networkMode: "always",
})

queryClient.setMutationDefaults(UPDATE_SURVEY_SITE_MUTATION_KEY, {
  mutationFn: updateSurveySiteFn,
  networkMode: "always",
})

queryClient.setMutationDefaults(DELETE_SURVEY_SITE_MUTATION_KEY, {
  mutationFn: deleteSurveySiteFn,
  networkMode: "always",
})
